import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client/core';
import { useDispatch } from 'react-redux';
import { addToast } from "@Components/Common/Toasts/toasts.slice";
import { setRegion } from "@Components/Common/Region/region.slice";
import { getBrowserRegionDefaults } from '../../../../utilities/region';

export const UPDATE_PROFILE = gql`
  mutation UpdateProfile($name: String, $email: String, $first_name: String, $last_name: String, $phone: String, $gender: String, $dob: Date, $culture: String, $currency: String) {
    updateProfile(name: $name, email: $email, first_name: $first_name, last_name: $last_name, phone: $phone, gender: $gender, dob: $dob, culture: $culture, currency: $currency) {
      id
      name
      first_name
      last_name
      email
      phone
      gender
      dob
      is_verified
      culture
      currency
    }
  }
`;

export const CHANGE_PASSWORD = gql`
  mutation ChangePassword($current_password: String!, $new_password: String!) {
    changePassword(current_password: $current_password, new_password: $new_password) {
      id
    }
  }
`;

export const useSettings = (currentName: string, currentEmail: string, user: any) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');

  // Form states
  const defaults = getBrowserRegionDefaults();
  const [name, setName] = useState(currentName);
  const [email, setEmail] = useState(currentEmail);
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [gender, setGender] = useState(user?.gender || '');
  const [dob, setDob] = useState(user?.dob || '');
  const [culture, setCulture] = useState(user?.culture || defaults.culture);
  const [currency, setCurrency] = useState(user?.currency || defaults.currency);

  // Sync state when user data loads (handles race condition where settings page mounts before GetCurrentUser resolves)
  useEffect(() => {
    if (currentName !== "Loading...") setName(currentName);
    if (currentEmail !== "Loading...") setEmail(currentEmail);
    if (user) {
      setFirstName((prev: string) => user.first_name || prev);
      setLastName((prev: string) => user.last_name || prev);
      setPhone((prev: string) => user.phone || prev);
      setGender((prev: string) => user.gender || prev);
      setDob((prev: string) => user.dob || prev);
      setCulture((prev: string) => user.culture || prev || defaults.culture);
      setCurrency((prev: string) => user.currency || prev || defaults.currency);
    }
  }, [currentName, currentEmail, user]);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [updateProfile, { loading: updatingProfile }] = useMutation(UPDATE_PROFILE, {
    context: { skipPersistedQueries: true }
  });
  const [changePassword, { loading: changingPassword }] = useMutation(CHANGE_PASSWORD, {
    context: { skipPersistedQueries: true }
  });

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateProfile({ variables: { name, email, first_name: firstName, last_name: lastName, phone, gender, dob: dob || null, culture, currency } });
      dispatch(setRegion({ culture, currency }));
      dispatch(addToast({ message: 'Profile updated successfully', type: 'success' }));
    } catch (err: any) {
      dispatch(addToast({ message: err.message || 'Failed to update profile', type: 'error' }));
    }
  };


  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await changePassword({ variables: { current_password: currentPassword, new_password: newPassword } });
      dispatch(addToast({ message: 'Password changed successfully', type: 'success' }));
      setCurrentPassword('');
      setNewPassword('');
    } catch (err: any) {
      dispatch(addToast({ message: err.message || 'Failed to change password', type: 'error' }));
    }
  };

  const userInitials = (firstName && lastName) 
    ? `${firstName[0]}${lastName[0]}`.toUpperCase() 
    : name.substring(0, 2).toUpperCase();

  return {
    activeTab,
    setActiveTab,
    name,
    setName,
    email,
    setEmail,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    phone,
    setPhone,
    gender,
    setGender,
    dob,
    setDob,
    culture,
    setCulture,
    currency,
    setCurrency,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    updatingProfile,
    changingPassword,
    handleUpdateProfile,
    handleChangePassword,
    userInitials,
  };
};
